/**
 * EditThreadActivityModal — combined date + note editor for a single Thread Activity log entry.
 * Replaces the old two separate "Edit note" / "Edit date" promptDialog prompts with one small
 * modal that saves both fields together.
 *
 * Opens via CustomEvent('open-edit-thread-activity-modal', { detail: {
 *   activityId, threadId, currentDate, currentNote
 * } }).
 * Patches the in-memory ctx.threadActCache[threadId] row + dispatches outline-render on success.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { updateThreadActivity, updateThread } from '../services/threads.js'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const areaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical', lineHeight: 1.5, fontFamily: '"DM Sans",sans-serif' }

export default function EditThreadActivityModal() {
  const [open, setOpen]           = useState(false)
  const [activityId, setActivityId] = useState(null)
  const [activityType, setActivityType] = useState('')
  const [threadId, setThreadId]   = useState(null)
  const [date, setDate]           = useState('')
  const [note, setNote]           = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setActivityId(d.activityId || null)
      setActivityType(d.activityType || '')
      setThreadId(d.threadId || null)
      setDate(d.currentDate || '')
      setNote(d.currentNote || '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-edit-thread-activity-modal', onOpen)
    return () => window.removeEventListener('open-edit-thread-activity-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    if (!date) { setError('Date is required.'); return }
    setSubmitting(true); setError(null)
    const trimmedNote = note.trim()
    try {
      await updateThreadActivity(activityId, { 'Activity Date': date, 'Notes': trimmedNote || null })

      // Thread Opened's note IS the thread description — keep them synced, same as the old
      // single-field "Edit note" flow did. Update the cache in place, not a full cache bust —
      // that would strand any view still rendering from ctx.threadCache mid-render.
      if (activityType === 'Thread Opened') {
        try {
          await updateThread(threadId, { 'Description': trimmedNote })
          if (Array.isArray(ctx.threadCache)) { const t = ctx.threadCache.find(x => x.id === threadId); if (t && t.fields) t.fields['Description'] = trimmedNote }
        } catch (_) { /* non-fatal */ }
      }

      const arr = ctx.threadActCache && ctx.threadActCache[threadId]
      if (Array.isArray(arr)) {
        const row = arr.find(a => a.id === activityId)
        if (row) { row.date = date; row.notes = trimmedNote }
      }
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="EditThreadActivityModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 420, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Edit Activity</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" style={inputStyle} value={date} onChange={e => setDate(e.target.value)} autoFocus />
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <textarea style={areaStyle} value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note…" />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-mid)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
